import React, { memo } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { Box, Flex } from '@qga/roo-ui/components';
import CheckAvailability from 'components/PropertyPage/PropertyAvailability/RoomTypes/RoomTypeList/CheckAvailability';
import TermsAndConditionsModal from '../CheckAvailability/components/TermsAndConditionsModal';
import UnavailableRoomType from 'components/PropertyPage/PropertyAvailability/RoomTypes/RoomTypeList/UnavailableRoomType';
import RoomTypeDetailsGallery from './components/RoomTypeDetailsGallery';
import { RoomTypeWrapper, OffersWrapper, CheckAvailabilityList } from './components/primitives';
import { getAllAvailableOffers, getExclusiveOffer } from 'store/exclusiveOffer/exclusiveOfferSelectors';
import { sortBy, keys, partition, isEmpty } from 'lodash';
import { useBreakpoints } from 'hooks/useBreakpoints';
import Offer from '../Offer';

const OffersList = ({ roomName, offers, roomTypeId, exclusiveOffer = false }) => {
  const totalOffers = offers.length;
  const { isLessThanBreakpoint } = useBreakpoints();
  const isMobile = isLessThanBreakpoint(0);

  return (
    <OffersWrapper
      mt={[0, 0, 10]}
      pt={[0, 3]}
      border={isMobile ? 2 : 0}
      borderColor="greys.porcelain"
      exclusiveOffer={exclusiveOffer}
      data-testid="offer-wrapper"
    >
      {offers.map((offer, counter) => (
        <Offer
          roomTypeId={roomTypeId}
          offer={offer}
          counter={counter + 1}
          key={offer.id}
          roomName={roomName}
          mb={4}
          totalOffers={totalOffers}
          data-testid="offer"
        />
      ))}
    </OffersWrapper>
  );
};
OffersList.propTypes = {
  roomName: PropTypes.string.isRequired,
  offers: PropTypes.array.isRequired,
  roomTypeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  smallTablet: PropTypes.bool,
  exclusiveOffer: PropTypes.bool,
};

const RoomType = ({ roomType, hasQuery = false }) => {
  const { name, offers, exclusiveOffers, id, termsAndConditions } = roomType;
  const hasOffers = !!(offers && offers.length);
  const hasExclusiveOffers = !!exclusiveOffers?.length;
  const isUnavailable = hasQuery && !hasOffers;
  const exclusiveOffer = useSelector(getExclusiveOffer);
  const isExclusiveOffer = !isEmpty(exclusiveOffer);

  const { isLessThanBreakpoint } = useBreakpoints();
  const isMobile = isLessThanBreakpoint(0);
  const availableOffers = useSelector(getAllAvailableOffers);
  const [available, unavailable] = partition(exclusiveOffers, (exclusiveOffer) => keys(availableOffers).includes(exclusiveOffer.id));
  const sortedAvailable = sortBy(available, (exclusiveOffer) => parseInt(availableOffers[exclusiveOffer.id]?.charges.total.amount ?? '0'));
  const sortedUnavailable = sortBy(unavailable, (exclusiveOffer) => parseInt(exclusiveOffer.offerTotal?.amount ?? '0'));
  const sortedExclusiveOffers = [...sortedAvailable, ...sortedUnavailable];
  const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const smallTablet = windowWidth > 750 && windowWidth < 1020;
  const weirdLaptopSizes = windowWidth >= 1100 && windowWidth < 1257;

  if (isUnavailable) {
    return <UnavailableRoomType roomType={roomType} hasQuery={hasQuery} />;
  }

  return (
    <Box mb={[0, 4, 8]}>
      <RoomTypeWrapper
        isMobile={isMobile}
        smallTablet={smallTablet}
        exclusiveOffer={isExclusiveOffer}
        pl={isExclusiveOffer ? [0, 0, 10] : [0, 0, 6]}
        pr={isExclusiveOffer ? 0 : [0, 0, 6]}
        pb={[2, 0]}
        pt={isExclusiveOffer ? [0, 2, 8] : [0, 6]}
      >
        <Flex width={['100%', '100%', '321px']} flexDirection={'column'}>
          <RoomTypeDetailsGallery roomType={roomType} hasOffers={hasOffers} smallTablet={smallTablet} exclusiveOffer={isExclusiveOffer} />
        </Flex>
        <Flex
          mx={smallTablet || isExclusiveOffer ? 0 : [0, 8]}
          width={['100%', '100%', weirdLaptopSizes ? `${windowWidth - 460}px` : '800px']}
          alignItems="flex-start"
          px={[6, 0]}
        >
          {hasOffers && (
            <OffersList roomName={name} offers={offers} roomTypeId={id} smallTablet={smallTablet} exclusiveOffer={isExclusiveOffer} />
          )}

          {hasExclusiveOffers && (
            <CheckAvailabilityList data-testid="check-availability-list">
              {sortedExclusiveOffers.map((exclusiveOffer, i) => (
                <CheckAvailability key={exclusiveOffer.id} room={roomType} offer={exclusiveOffer} index={i} />
              ))}
            </CheckAvailabilityList>
          )}
          {!hasExclusiveOffers && !hasQuery && <CheckAvailability room={roomType} />}
        </Flex>
      </RoomTypeWrapper>
      {termsAndConditions && <TermsAndConditionsModal roomName={name} termsAndConditions={termsAndConditions} />}
    </Box>
  );
};

RoomType.propTypes = {
  roomType: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    offerId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string.isRequired,
    description: PropTypes.string,
    roomTypeFacilities: PropTypes.array.isRequired,
    offers: PropTypes.array,
    exclusiveOffers: PropTypes.array,
    maxOccupantCount: PropTypes.number,
    images: PropTypes.array,
    mainImage: PropTypes.shape({
      urlMedium: PropTypes.string,
      caption: PropTypes.string,
    }),
    title: PropTypes.string,
    termsAndConditions: PropTypes.string,
  }).isRequired,
  hasQuery: PropTypes.bool,
};

export default memo(RoomType);
